import { ActionTree } from 'vuex';
import { getDefaultState } from './chat.state';
import { IChatState, EChatAction, EChatMutation } from '@/dealroom-view/chat/store/chat.types';
import { RootState } from '@/store/store';
import { translate } from '@/helpers/translation.helper';
import { ChatService } from '@/api/chat/services/chat.service';
import { IComment } from '@/dealroom-view/chat/types/comment';
import bugsnagClient from '@/bugsnag';
import { EmojiHelper } from '@/chat/helpers/emoji.helper';

export const actions: ActionTree<IChatState, RootState> = {
  async [EChatAction.LoadComments]({ dispatch }, documentId: string) {
    try {
      const comments: IComment[] = await ChatService.getComments(documentId);
      dispatch(EChatAction.LoadCommentsSuccess, comments);
    } catch (error) {
      dispatch(EChatAction.LoadCommentsFailed);
    }
  },
  async [EChatAction.LoadCommentsSuccess]({ commit, dispatch, state }, comments: IComment[]) {
    if (!state.commentsRead) {
      commit(EChatMutation.SetCommentsRead, comments);
    }

    commit(EChatMutation.SetLoaded, true);
    dispatch(EChatAction.SetChatMessages, comments);
  },
  async [EChatAction.LoadCommentsFailed]({ commit, dispatch }) {
    commit(EChatMutation.SetLoaded, true);
    dispatch('alert/showDangerAlert', translate('failed-to-load-chat-messages'), {
      root: true,
    });
  },
  async [EChatAction.CreateComment](
    { dispatch },
    { commentText, documentId }: { commentText: string; documentId: string }
  ) {
    try {
      await ChatService.createComment(documentId, commentText);
    } catch (e) {
      dispatch(EChatAction.CreateCommentFailed);
    }
  },
  async [EChatAction.CreateCommentFailed]({ dispatch }, error) {
    if (bugsnagClient) {
      bugsnagClient.notify(error);
    }
    dispatch('alert/showDangerAlert', translate('failed-to-send-message'), {
      root: true,
    });
  },
  async [EChatAction.DeleteComment](
    { dispatch, commit, state },
    { commentId, documentId }: { commentId: string; documentId: string }
  ) {
    const { comments }: IChatState = state;
    try {
      await ChatService.deleteComment(documentId, commentId);
      const commentIndex: number = comments.findIndex(
        (comment: IComment) => comment.id === commentId
      );
      const newComments: IComment[] = [...comments];
      newComments.splice(commentIndex, 1);
      commit(EChatMutation.SetComments, newComments);
    } catch (error) {
      dispatch(EChatAction.DeleteCommentFailed, { error, comments });
    }
  },
  async [EChatAction.DeleteCommentFailed](
    { dispatch, commit },
    { error, comments }: { error: Error; comments: IComment[] }
  ) {
    if (bugsnagClient) {
      bugsnagClient.notify(error);
    }
    dispatch('alert/showDangerAlert', translate('failed-to-delete-message'), {
      root: true,
    });
    commit(EChatMutation.SetComments, comments);
  },
  [EChatAction.SetChatMessages]({ commit }, comments: IComment[]) {
    const parsedComments: IComment[] = [];

    comments.forEach((comment: IComment) => {
      const parsedCommentText: string = EmojiHelper.emojify(comment.commentText);
      parsedComments.push({ ...comment, commentText: parsedCommentText });
    });
    commit(EChatMutation.SetComments, parsedComments);
  },
  [EChatAction.ResetState]({ commit }) {
    commit(EChatMutation.SetState, getDefaultState());
  },
  [EChatAction.AddComment]({ dispatch, state }, newComment: IComment) {
    const alreadyExist: boolean = state.comments.some(
      (comment: IComment) => comment.id === newComment.id
    );
    if (alreadyExist) {
      return;
    }
    dispatch(EChatAction.SetChatMessages, [...state.comments, newComment]);
  },
};
